import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Loader2, ArrowLeft, Mail, Phone, CheckCircle2 } from "lucide-react";
import ProfawzLogo from "../components/ProfawzLogo";

export default function ForgotPassword() {
    const [identifier, setIdentifier] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const [debugUrl, setDebugUrl] = useState(""); // shown in dev when Resend not configured

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!identifier.trim()) {
            setError("Renseignez votre e-mail ou votre numéro de téléphone.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { identifier: identifier.trim() });
            setDone(true);
            if (data?.debug_reset_url) setDebugUrl(data.debug_reset_url);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper flex flex-col">
            <header className="border-b border-rule">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
                    <ProfawzLogo size={28} />
                    <div>
                        <div className="h-display text-base font-bold">Profawz</div>
                        <div className="text-[10px] font-mono text-muted2 uppercase tracking-wider">Mot de passe oublié</div>
                    </div>
                </div>
            </header>
            <main className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <Link to="/login" className="text-xs font-mono text-muted2 inline-flex items-center gap-1 hover:text-ink mb-4" data-testid="forgot-back-login">
                        <ArrowLeft size={12} /> Retour à la connexion
                    </Link>

                    {done ? (
                        <div className="panel p-8 text-center" data-testid="forgot-done">
                            <CheckCircle2 size={42} className="text-emerald-500 mx-auto mb-3" />
                            <h1 className="h-display text-2xl font-semibold">Demande enregistrée</h1>
                            <p className="text-sm text-muted2 mt-3 max-w-sm mx-auto">
                                Si un compte correspond à <strong>{identifier}</strong>, un lien de réinitialisation vient d'être envoyé par e-mail. Le lien est valable <strong>1 heure</strong>.
                            </p>
                            {debugUrl && (
                                <div className="mt-6 p-3 bg-amber-50 border border-amber-200 text-left text-xs">
                                    <div className="font-semibold text-amber-800 mb-1">Mode développement (e-mail non configuré)</div>
                                    <p className="text-amber-700 mb-2">Lien direct :</p>
                                    <a href={debugUrl} className="font-mono text-amber-900 break-all underline" data-testid="forgot-debug-link">{debugUrl}</a>
                                </div>
                            )}
                            <Link to="/login" className="btn-outline mt-6 inline-flex">
                                Retour à la connexion
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="label-eyebrow mb-2">Réinitialiser le mot de passe</div>
                            <h1 className="h-display text-3xl font-semibold mb-2">Mot de passe oublié ?</h1>
                            <p className="text-sm text-muted2 mb-6">
                                Renseignez votre <strong>e-mail</strong> ou votre <strong>numéro de téléphone</strong>. Vous recevrez un lien sécurisé par e-mail pour choisir un nouveau mot de passe.
                            </p>

                            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
                                <div>
                                    <label className="label-eyebrow mb-2 block">E-mail ou téléphone</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2">
                                            {identifier.includes("@") ? <Mail size={14} /> : <Phone size={14} />}
                                        </span>
                                        <input
                                            type="text"
                                            value={identifier}
                                            onChange={(e) => setIdentifier(e.target.value)}
                                            className="field-input !pl-9"
                                            placeholder="vous@email.fr ou 06 12 34 56 78"
                                            required
                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            spellCheck="false"
                                            autoComplete="email"
                                            data-testid="forgot-identifier-input"
                                        />
                                    </div>
                                    <p className="text-[11px] text-muted2 mt-1.5">
                                        Le lien de réinitialisation est toujours envoyé à l'adresse e-mail associée au compte.
                                    </p>
                                </div>

                                {error && (
                                    <div className="text-sm text-destructive border border-destructive/20 bg-destructive/5 px-3 py-2 rounded-sm" data-testid="forgot-error">
                                        {error}
                                    </div>
                                )}

                                <button type="submit" disabled={loading} className="btn-primary w-full !py-3" data-testid="forgot-submit-button">
                                    {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                                    {loading ? "Envoi en cours…" : "Recevoir le lien de réinitialisation"}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}
